import { Calendar } from '@untitledui/icons'
import clsx from 'clsx'
import AuditTab from 'components/billing/billingRun/AuditTab'
import RunTab from 'components/billing/billingRun/RunTab'
import MonthPicker from 'components/billing/MonthPicker'
import Page from 'components/ui/Page'
import { format, subMonths } from 'date-fns'
import { useAppSelector } from 'hooks/hooks'
import { useSearchParams } from 'react-router-dom'

type TabId = 'run' | 'audit'

const TABS: { id: TabId; label: string; hint: string }[] = [
    { id: 'run', label: 'Billing run', hint: 'Trigger the billing steps & follow progress' },
    { id: 'audit', label: 'Audit', hint: 'Verify a month completed correctly' },
]

/** On the 1st, Finance invoices the previous month — that's the default. */
const defaultMonth = () => format(subMonths(new Date(), 1), 'yyyy-MM')

/**
 * Server-side billing run (TRUS-8356): manual triggers for
 * generate invoices / send mails / create transactions, run history with
 * readable error reports, and the per-month audit overview.
 */
const BillingRunPage = () => {
    // Tab + month live in the URL so views are shareable and survive a
    // refresh. The countries come from the office-wide country flags (left
    // sidebar): the whole page — buttons, cards, history, audit — is scoped
    // to that selection.
    const [searchParams, setSearchParams] = useSearchParams()
    const activeTab: TabId = searchParams.get('tab') === 'audit' ? 'audit' : 'run'
    const month = searchParams.get('month') || defaultMonth()
    const { countries } = useAppSelector((state) => state.countries)

    const setParam = (key: string, value: string) => {
        setSearchParams(
            (prev) => {
                prev.set(key, value)
                return prev
            },
            { replace: true },
        )
    }

    return (
        <Page title="Billing run">
            <div className="mx-auto max-w-screen-2xl px-1 py-6">
                <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-semibold text-dark">Billing run</h1>
                    {countries.map((country) => (
                        <span
                            key={country}
                            className="rounded-full bg-primary-50 px-2.5 py-0.5 text-md font-medium text-primary-700">
                            {country}
                        </span>
                    ))}
                    <div className="ml-auto flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 shadow-xs ring-1 ring-neutral-200">
                        <Calendar className="h-4 w-4 text-neutral-400" />
                        <MonthPicker
                            value={month}
                            onChange={(value: string) => setParam('month', value || defaultMonth())}
                            nrMonths={24}
                            startYearMonth={undefined}
                        />
                    </div>
                </div>
                <p className="mt-1 text-md text-neutral-500">
                    Runs the billing steps server-side — no browser tab that has to stay open. Each
                    step is safe to re-run: it only picks up what is still missing. Everything on
                    this page follows the country flags in the sidebar — e.g. on DE you can start
                    transactions while NL is still busy with mails.
                </p>

                <div className="mt-6 flex gap-1 border-b border-neutral-200">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setParam('tab', tab.id)}
                            className={clsx(
                                '-mb-px border-b-2 px-4 py-3 text-left transition',
                                activeTab === tab.id
                                    ? 'border-primary'
                                    : 'border-transparent hover:bg-neutral-50',
                            )}>
                            <span
                                className={clsx(
                                    'block text-md font-semibold',
                                    activeTab === tab.id ? 'text-primary' : 'text-neutral-700',
                                )}>
                                {tab.label}
                            </span>
                            <span className="block text-sm text-neutral-400">{tab.hint}</span>
                        </button>
                    ))}
                </div>

                <div className="mt-6">
                    {activeTab === 'run' ? <RunTab month={month} /> : <AuditTab month={month} />}
                </div>
            </div>
        </Page>
    )
}

export default BillingRunPage
